'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import {
  Award,
  BadgeCheck,
  Building2,
  Camera,
  CheckCircle2,
  MapPin,
  Medal,
  Store,
  Trophy,
  Users,
} from 'lucide-react'
import {
  ContentCard,
  HeroSecondary,
  IconOrb,
  MetricCard,
  PublicBadge,
  PublicGrid,
  PublicPage,
  PublicSection,
  TrustRail,
} from '../../components/public/PublicDesign'
import VolunteerMap from '../../components/volunteers/VolunteerMap'

const pointRewards = [
  ['List a new masjid', '+10 pts', Building2],
  ['Listing gets approved', '+15 pts', BadgeCheck],
  ['Masjid claims your listing', '+20 pts', Trophy],
  ['Upload a useful photo', '+2 pts', Camera],
] as const

export default function VolunteersPage() {
  // H5: no DEMO_VOLUNTEERS — honest-empty leaderboard until real volunteer data exists.
  const leaders: Array<{ id: string; photo?: string; name: string; city: string; badge: string; points: number }> = []
  /* honest signals from the stats engine — no invented activity counts */
  const [stats, setStats] = useState<{ mapped: number; verified: number; cities: number } | null>(null)
  useEffect(() => {
    let alive = true
    fetch('/api/stats').then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive && d?.mapped) setStats(d) }).catch(() => {})
    return () => { alive = false }
  }, [])

  return (
    <PublicPage className="hero-art-dome">
      <main>
        <HeroSecondary
          eyebrow="Community mapping"
          title="Help map and strengthen masajid across India."
          description="Join volunteer missions at masjids near you — Ramadan drives, funeral prayer support, education and maintenance — and help communities keep public profiles accurate. Track your contribution impact."
          actions={[
            // 'Start Volunteering' builds a personal volunteer profile; 'Apply to serve in your area'
            // joins the volunteer network with a geographic scope (state/district/city). Both surfaced.
            { label: 'Start Volunteering', href: '/volunteer/profile', variant: 'primary' },
            { label: 'Apply to serve in your area', href: '/volunteer/apply', variant: 'secondary' },
            { label: 'Find a Masjid', href: '/masjids', variant: 'secondary' },
          ]}
        >
          <TrustRail
            verified="Contribution reviewed"
            claimed="Community linked"
            updated="This week"
            score={stats ? `${stats.mapped.toLocaleString('en-IN')} listings` : 'Civic impact'}
            completeness={stats ? Math.round((stats.verified / Math.max(1, stats.mapped)) * 100) : 0}
            reportHref="/contact"
          />
        </HeroSecondary>

        <PublicSection
          eyebrow="Contribution heat map"
          title="See where volunteer attention is needed."
          description="Locality signals connect profile mapping, facility updates, community service, and verification work."
        >
          <VolunteerMap />
        </PublicSection>

        <PublicSection eyebrow="Volunteer leaders" title="Community contributions with visible impact.">
          <div className="mx-rail-grid">
            <div className="mx-row-list">
              {leaders.length === 0 ? (
                // Honest-empty: no fabricated leaders. Invite the visitor to be the first, and
                // point them at the contribution actions below.
                <ContentCard>
                  <IconOrb icon={Award} />
                  <h3>No volunteer contributions yet</h3>
                  <p>Be the first to make an impact — map a masjid, update its facilities, or verify a listing, and you’ll appear here.</p>
                  <Link href="/volunteer/profile" className="mx-button">Start volunteering</Link>
                </ContentCard>
              ) : leaders.map((volunteer, index) => (
                <ContentCard key={volunteer.id} className="mx-leader-row">
                  <span className="mx-rank">{index + 1}</span>
                  {volunteer.photo
                    // eslint-disable-next-line @next/next/no-img-element
                    ? <img src={volunteer.photo} alt={volunteer.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    : <span aria-hidden style={{ width: 40, height: 40, borderRadius: '50%', background: '#E8F5F0', color: '#0a3d2e', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{volunteer.name.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}</span>}
                  <div>
                    <h3>{volunteer.name}</h3>
                    <p><MapPin size={13} /> {volunteer.city} - {volunteer.badge}</p>
                  </div>
                  <MetricCard value={String(volunteer.points)} label="Contribution points" icon={Award} />
                </ContentCard>
              ))}
            </div>
            <ContentCard>
              <IconOrb icon={Users} />
              <h3>How contributions work</h3>
              <div className="mx-row-list">
                {pointRewards.map(([label, value, Icon]) => (
                  <div key={label} className="mx-operation-row">
                    <Icon size={16} />
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
              <PublicBadge tone="emerald"><CheckCircle2 size={13} /> Reviewed before publishing</PublicBadge>
            </ContentCard>
          </div>
        </PublicSection>

        <PublicSection eyebrow="Contribution paths" title="Choose one useful action.">
          <PublicGrid columns={3}>
            <ContentCard>
              <IconOrb icon={Building2} />
              <h3>Map a masjid</h3>
              <p>Add an accurate name, location, and public profile for community review.</p>
              <Link href="/onboard" className="mx-button ghost">Register masjid</Link>
            </ContentCard>
            <ContentCard>
              <IconOrb icon={Camera} />
              <h3>Improve a profile</h3>
              <p>Share a current public photo or correct outdated facility information.</p>
              <Link href="/masjids" className="mx-button ghost">Find profiles</Link>
            </ContentCard>
            <ContentCard>
              <IconOrb icon={Store} tone="gold" />
              <h3>Support local service</h3>
              <p>Join Ramadan, funeral prayer, education, and maintenance activity near you.</p>
              <Link href="/feed" className="mx-button ghost">View Ummah feed</Link>
            </ContentCard>
          </PublicGrid>
        </PublicSection>

      </main>
    </PublicPage>
  )
}
